import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchIdentitiesById } from 'common/services/api/Identities/Identities.thunk';
import { IdentityOemMapping } from 'common/types/Entities';

import type { RootState } from '../../app/store';

type UserProfileIdentitiesSliceState = {
    userProfiles: IdentityOemMapping[];
    processing: boolean;
    errorCode?: number;
};

const initialState: UserProfileIdentitiesSliceState = {
    userProfiles: [],
    processing: false
};

const userProfileIdentitiesSlice = createSlice({
    name: 'userProfileIdentities',
    initialState,
    reducers: () => ({}),

    extraReducers: builder => {
        builder.addCase(fetchIdentitiesById.pending, state => {
            state.processing = true;
            state.userProfiles = [];
        });
        builder.addCase(fetchIdentitiesById.fulfilled, (state, action: PayloadAction<IdentityOemMapping[]>) => {
            state.processing = false;
            state.userProfiles = action.payload;
        });
        builder.addCase(fetchIdentitiesById.rejected, (state, error) => {
            state.processing = false;
            state.errorCode = extractStatusCode(error.error.message ?? '');
            state.userProfiles = [];
        });
    }
});

// eslint-disable-next-line no-restricted-syntax
export default userProfileIdentitiesSlice.reducer;
export const selectIsProcessing = (state: RootState) => state.userProfileIdentities.processing;
export const selectUserProfiles = createSelector(
    [(state: RootState) => state.userProfileIdentities.userProfiles],
    (userProfiles) => [...userProfiles].sort(compareIdentities)
);

// Helper functions
export function compareIdentities(a: IdentityOemMapping, b: IdentityOemMapping) {
    // Sort users without oemIdPName to the end
    if (!a.oemIdPName && b.oemIdPName) return 1;
    if (a.oemIdPName && !b.oemIdPName) return -1;

    // If oemIdPName is present for both, compare them
    if (a.oemIdPName && b.oemIdPName) {
        const idpComparison = a.oemIdPName.localeCompare(b.oemIdPName);
        if (idpComparison !== 0) return idpComparison;
    }

    // Sort users without email to the end
    if (!a.email && b.email) return 1;
    if (a.email && !b.email) return -1;

    // If email is present for both, compare them
    if (a.email && b.email) {
        return a.email.localeCompare(b.email);
    }

    // They're equal in terms of sorting
    return 0;
}
